<template>
    <div v-if="showFindInLayers" class="find-in-layers-container">
        <div class="find-in-layers-panel">
            <div class="search-input-wrapper">
                <c-input
                    ref="inputComponentRef"
                    v-model="searchQueryModel"
                    input-class="search-input"
                    type="text"
                    autocomplete="off"
                    :placeholder="'Find in Layers (name or layer ID)'"
                    :debounce="500"
                    @debounce="onUpdateSearchDecounced"
                >
                    <template #append>
                        <span class="result-counter">{{ currentResultNumber }} of {{ totalCount }}</span>
                    </template>
                </c-input>
            </div>

            <div class="action-buttons">
                <c-button
                    class="btn-icon"
                    size="icon"
                    variant="link"
                    :icon="['fas', 'chevron-up']"
                    :disabled="!hasResults"
                    title="Focus Previous"
                    @click.stop="focusPrevious"
                />

                <c-button
                    class="btn-icon"
                    size="icon"
                    variant="link"
                    :icon="['fas', 'chevron-down']"
                    :disabled="!hasResults"
                    title="Focus Next"
                    @click.stop="focusNext"
                />

                <div class="divider" />

                <c-button
                    class="btn-icon close-btn"
                    size="icon"
                    variant="link"
                    :icon="['fas', 'times']"
                    title="Close"
                    @click.stop="handleClose"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onUnmounted, Ref } from 'vue';
import store from '@/store';
import type { ListElements } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import { getCanvasElementTitle } from '@/utils/elementsUtils';
import CButton from '@/components/shared/CButton/CButton.vue';
import CInput from '@/components/shared/CInput';

// Types
type ListElementsWithMatch = ListElements & { matched?: boolean };

interface CInputRef {
    focus: () => void;
}

// Props & Emits
const emit = defineEmits<{
    (event: 'close'): void;
}>();

const props = withDefaults(
    defineProps<{
        enable?: boolean;
    }>(),
    {
        enable: true
    }
);

// State
const searchQueryModel = ref('');
const searchQuery = ref('');
const selectedLayerIndex = ref(-1);
const showFindInLayers = ref(false);
const inputComponentRef: Ref<CInputRef | null> = ref(null);

// Computed
const treeItems = computed(() => store.state.storeLayers?.treeItems || []);

const filteredLayers = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    return query ? filterTreeItems(treeItems.value, query) : treeItems.value;
});

const totalCount = computed(() => countMatchedItems(filteredLayers.value));
const hasResults = computed(() => totalCount.value > 0);
const currentResultNumber = computed(() => Math.max(selectedLayerIndex.value + 1, 0));

// Helper Functions
const getElementName = (item: ListElements): string => {
    if (item.element?.name) return item.element.name;
    if (item.type === ListElementType.CanvasElement && item.element) {
        return getCanvasElementTitle(item.element) || '';
    }
    return '';
};

const getElementNumericId = (item: ListElements): string => {
    return item.element?.id != null ? String(item.element.id) : '';
};

const filterTreeItems = (items: ListElementsWithMatch[], query: string): ListElementsWithMatch[] => {
    return items.reduce<ListElementsWithMatch[]>((acc, item) => {
        const name = getElementName(item).toLowerCase();
        const numericId = getElementNumericId(item);
        const nameMatches = name.includes(query);
        // Match by the numeric Layer ID shown in the panel, exact (so "3" finds only ID 3).
        const idMatches = numericId.length > 0 && numericId === query;
        const matches = nameMatches || idMatches;
        const filteredChildren = item.children?.length ? filterTreeItems(item.children, query) : [];

        if (matches || filteredChildren.length > 0) {
            acc.push({
                ...item,
                matched: matches && item.type !== ListElementType.PageEps,
                children: filteredChildren
            });
        }

        return acc;
    }, []);
};

const countMatchedItems = (items: ListElementsWithMatch[]): number => {
    return items.reduce((count, item) => {
        const itemCount = item.matched ? 1 : 0;
        const childrenCount = item.children?.length ? countMatchedItems(item.children) : 0;
        return count + itemCount + childrenCount;
    }, 0);
};

const getMatchedItemByIndex = (items: ListElementsWithMatch[], targetIndex: number): ListElementsWithMatch | null => {
    let currentIndex = -1;

    const findItem = (items: ListElementsWithMatch[]): ListElementsWithMatch | null => {
        for (const item of items) {
            if (item.matched && ++currentIndex === targetIndex) {
                return item;
            }

            if (item.children?.length) {
                const found = findItem(item.children);
                if (found) return found;
            }
        }
        return null;
    };

    return findItem(items);
};

// Actions
const selectLayerAtIndex = (index: number) => {
    if (index < 0 || !hasResults.value) return;

    const matchedItem = getMatchedItemByIndex(filteredLayers.value, index);
    if (!matchedItem?.element) return;

    const elementId = String(matchedItem.element.uuid || matchedItem.element.id);
    if (!elementId) return;

    store.dispatch('storeLayers/expandAndSelectElement', {
        elementId,
        scrollToElement: true
    });
};

const navigateResults = (direction: 'next' | 'prev') => {
    if (!hasResults.value) return;

    if (direction === 'next') {
        selectedLayerIndex.value = selectedLayerIndex.value >= totalCount.value - 1 ? 0 : selectedLayerIndex.value + 1;
    } else {
        selectedLayerIndex.value = selectedLayerIndex.value <= 0 ? totalCount.value - 1 : selectedLayerIndex.value - 1;
    }
};

const focusNext = () => navigateResults('next');
const focusPrevious = () => navigateResults('prev');

const resetSearch = () => {
    searchQuery.value = '';
    searchQueryModel.value = '';
    selectedLayerIndex.value = -1;
};

const handleClose = () => {
    showFindInLayers.value = false;
    emit('close');
};

const onUpdateSearchDecounced = (value: string | number) => {
    searchQuery.value = String(value);
};

const openFindPanel = () => {
    showFindInLayers.value = true;
    requestAnimationFrame(() => {
        inputComponentRef.value?.focus();
    });
};

const closeFindPanel = () => {
    showFindInLayers.value = false;
};

const toggleFindPanel = () => {
    if (showFindInLayers.value) {
        closeFindPanel();
    } else {
        openFindPanel();
    }
};

// Event Handlers
const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        toggleFindPanel();
    }
};

// Watchers
watch(filteredLayers, () => {
    selectedLayerIndex.value = -1;
    if (hasResults.value) {
        requestAnimationFrame(focusNext);
    }
});

watch(selectedLayerIndex, (newIndex) => {
    if (newIndex >= 0 && hasResults.value) {
        selectLayerAtIndex(newIndex);
    }
});

watch(showFindInLayers, (isVisible) => {
    if (!isVisible) {
        resetSearch();
    }
});

// Lifecycle
onMounted(() => {
    if (props.enable) {
        window.addEventListener('keydown', handleKeyDown);
    }
});

onUnmounted(() => {
    if (props.enable) {
        window.removeEventListener('keydown', handleKeyDown);
    }
});

defineExpose({
    open: openFindPanel,
    close: closeFindPanel,
    toggle: toggleFindPanel,
    isOpen: () => showFindInLayers.value
});
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';
$primary-color: #0066cc;
$border-color: $gray-light;
$background-color: #fefefe;
$hover-background: #f5f5f5;
$text-color: $gray-600;
$text-muted: #666;

.find-in-layers-container {
    width: 100%;
    max-width: 430px;
    background: $background-color;
    border-radius: 0 0 8px 8px;
    border-right: 1px solid $border-color;
    border-bottom: 1px solid $border-color;
    border-left: 1px solid $border-color;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    position: absolute;
    top: 53px;
    right: 0;
    z-index: 40;

    .find-in-layers-panel {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        gap: 8px;

        .search-input-wrapper {
            flex: 1;
            min-width: 0;
            position: relative;

            ::v-deep .search-input {
                background: $background-color;
                border: 1px solid $border-color;
                border-radius: 4px;
                padding: 8px 12px;
                font-size: 14px;
                color: $text-color;
                transition: all 0.2s ease;

                &::placeholder {
                    color: #999;
                    font-weight: 400;
                }

                &:focus {
                    border-color: $primary-color;
                    outline: none;
                    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
                    padding-right: 50px;
                }
            }

            .result-counter {
                //position: absolute;
                //right: 12px;
                //top: 50%;
                //transform: translateY(-50%);
                font-size: 12px;
                color: $text-muted;
                font-weight: 500;
                white-space: nowrap;
                pointer-events: none;
            }

            ::v-deep .input-group-append {
                align-items: center;
                border: none !important;
                position: absolute;
                z-index: 3;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
            }
        }

        .action-buttons {
            display: flex;
            gap: 4px;
            align-items: center;

            .btn-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                padding: 0;
                background: transparent;
                border: 1px solid transparent;
                border-radius: 4px;
                cursor: pointer;
                color: $text-muted;
                transition: all 0.2s ease;

                ::v-deep svg {
                    width: 18px;
                    height: 18px;
                    color: #333333;
                }

                &:hover {
                    background: $hover-background;
                    color: $text-color;
                    border-color: $border-color;
                }

                &:active {
                    background: #e8e8e8;
                }
            }

            .divider {
                width: 1px;
                height: 32px;
                background: $border-color;
            }
        }
    }
}
</style>
